/**
 * UserService - Secure user management following FAQ requirements
 * Handles user profiles, scene points, levels, and authentication
 */

import { supabase } from '../config/supabase';

class UserService {
  constructor() {
    this.currentUser = null;
    this.listeners = [];
  }

  // =============================================
  // USER PROFILE MANAGEMENT
  // =============================================

  /**
   * Create user profile from Supabase auth metadata
   * Called during registration process
   *
   * NOTE: Do NOT call this after registration. The trigger on auth.users will create the profile row automatically.
   */
  async createUserProfile(authUser, additionalData = {}) {
    try {
      const metadata = authUser.user_metadata || {};
      
      // Validate required fields
      const requiredFields = ['username', 'city', 'state', 'user_type'];
      for (const field of requiredFields) {
        if (!metadata[field] && !additionalData[field]) {
          throw new Error(`Missing required field: ${field}`);
        }
      }

      // Sanitize and validate input
      const userData = {
        id: authUser.id,
        email: authUser.email,
        username: this.sanitizeUsername(metadata.username || additionalData.username),
        user_type: this.validateUserType(metadata.user_type || additionalData.user_type),
        city: this.sanitizeText(metadata.city || additionalData.city),
        state: this.sanitizeText(metadata.state || additionalData.state),
        scene_points: 0,
        user_level: 'poser',
        is_admin: false,
        is_approved_advertiser: false
      };

      // Add location if coordinates provided
      if (metadata.latitude && metadata.longitude) {
        userData.location = `POINT(${metadata.longitude} ${metadata.latitude})`;
      }

      const { data, error } = await supabase
        .from('users')
        .insert([userData])
        .select()
        .single();

      if (error) {
        console.error('Error creating user profile:', error);
        throw new Error(`Failed to create user profile: ${error.message}`);
      }

      this.currentUser = data;
      this.notifyListeners();
      return { success: true, user: data };

    } catch (error) {
      console.error('Error in createUserProfile:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Load user profile by ID
   */
  async loadUserProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          id,
          username,
          email,
          display_name,
          bio,
          city,
          state,
          location,
          user_type,
          user_level,
          scene_points,
          is_approved_advertiser,
          created_at,
          updated_at
        `)
        .eq('id', userId)
        .single();

      if (error) {
        throw error;
      }

      return {
        success: true,
        user: {
          id: data.id,
          username: data.username,
          email: data.email,
          displayName: data.display_name,
          bio: data.bio,
          city: data.city,
          state: data.state,
          location: data.location,
          userType: data.user_type,
          userLevel: data.user_level,
          scenePoints: data.scene_points,
          isApprovedAdvertiser: data.is_approved_advertiser,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        }
      };

    } catch (error) {
      console.error('Error loading user profile:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update user profile
   */
  async updateUserProfile(userId, updates) {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({
          display_name: updates.displayName,
          bio: updates.bio,
          city: updates.city,
          state: updates.state,
          location: updates.location
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return {
        success: true,
        user: {
          id: data.id,
          username: data.username,
          email: data.email,
          displayName: data.display_name,
          bio: data.bio,
          city: data.city,
          state: data.state,
          location: data.location,
          userType: data.user_type,
          userLevel: data.user_level,
          scenePoints: data.scene_points,
          isApprovedAdvertiser: data.is_approved_advertiser,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        }
      };

    } catch (error) {
      console.error('Error updating user profile:', error);
      return { success: false, error: error.message };
    }
  }

  // =============================================
  // SCENE POINTS & LEVELS (from FAQ)
  // =============================================

  /**
   * Get user level info based on scene points
   */
  getUserLevel(points) {
    const levels = [
      { name: 'POSER', icon: '🤡', color: '#6B7280', min: 0, max: 19, approvalPower: 1, reportWeight: 1 },
      { name: 'Scene Supporter', icon: '📻', color: '#3B82F6', min: 20, max: 49, approvalPower: 1, reportWeight: 1 },
      { name: 'Active Member', icon: '🎧', color: '#10B981', min: 50, max: 99, approvalPower: 1, reportWeight: 1 },
      { name: 'Trusted Poster', icon: '🎵', color: '#8B5CF6', min: 100, max: 249, approvalPower: 1, reportWeight: 1 },
      { name: 'Scene Insider', icon: '🎤', color: '#CD7F32', min: 250, max: 499, approvalPower: 2, reportWeight: 3 },
      { name: 'Scene Veteran', icon: '🎸', color: '#C0C0C0', min: 500, max: 999, approvalPower: 2, reportWeight: 3 },
      { name: 'Scene Legend', icon: '👑', color: '#FFD700', min: 1000, max: Infinity, approvalPower: 3, reportWeight: 3 }
    ];

    return levels.find(level => points >= level.min && points <= level.max) || levels[0];
  }

  /**
   * Get scene points history for user
   */
  async getScenePointsHistory(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('scene_points_history')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw error;
      }

      return { success: true, history: data };

    } catch (error) {
      console.error('Error getting scene points history:', error);
      return { success: false, error: error.message };
    }
  }

  // =============================================
  // USER SEARCH & DISCOVERY
  // =============================================

  /**
   * Search users by username, email, or city
   */
  async searchUsers(query, limit = 20) {
    try {
      if (!query || query.length < 2) {
        return { success: true, users: [] };
      }

      const sanitizedQuery = this.sanitizeText(query);
      
      const { data, error } = await supabase
        .from('users')
        .select('id, username, email, city, state, user_level, scene_points, created_at')
        .or(`username.ilike.%${sanitizedQuery}%,email.ilike.%${sanitizedQuery}%,city.ilike.%${sanitizedQuery}%`)
        .order('scene_points', { ascending: false })
        .limit(limit);

      if (error) {
        throw error;
      }

      return { success: true, users: data };

    } catch (error) {
      console.error('Error searching users:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get users by location
   */
  async getUsersByLocation(city, state, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, username, city, state, user_level, scene_points, created_at')
        .eq('city', city)
        .eq('state', state)
        .order('scene_points', { ascending: false })
        .limit(limit);

      if (error) {
        throw error;
      }

      return { success: true, users: data };

    } catch (error) {
      console.error('Error getting users by location:', error);
      return { success: false, error: error.message };
    }
  }

  // =============================================
  // ADMIN FUNCTIONS
  // =============================================

  /**
   * Check if user is admin
   */
  isAdmin(user = this.currentUser) {
    return user && user.is_admin === true;
  }

  /**
   * Check if user can moderate (admin or 100+ points)
   */
  canModerate(user = this.currentUser) {
    return user && (user.is_admin === true || user.scene_points >= 100);
  }

  /**
   * Approve user as advertiser (admin only)
   */
  async approveAdvertiser(userId, adminUserId) {
    try {
      // Check admin permissions
      const adminUser = await this.loadUserProfile(adminUserId);
      if (!adminUser.success || !this.isAdmin(adminUser.user)) {
        throw new Error('Insufficient permissions');
      }

      const { data, error } = await supabase
        .from('users')
        .update({ is_approved_advertiser: true })
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return { success: true, user: data };

    } catch (error) {
      console.error('Error approving advertiser:', error);
      return { success: false, error: error.message };
    }
  }

  // =============================================
  // VALIDATION & SANITIZATION
  // =============================================

  /**
   * Sanitize username
   */
  sanitizeUsername(username) {
    if (!username) throw new Error('Username is required');
    
    // Remove special characters, keep alphanumeric and underscores
    const sanitized = username.trim().replace(/[^a-zA-Z0-9_]/g, '');
    
    if (sanitized.length < 3) {
      throw new Error('Username must be at least 3 characters');
    }
    
    if (sanitized.length > 50) {
      throw new Error('Username must be less than 50 characters');
    }
    
    return sanitized;
  }

  /**
   * Validate user type
   */
  validateUserType(userType) {
    const validTypes = ['fan', 'band', 'venue'];
    if (!validTypes.includes(userType)) {
      throw new Error('Invalid user type');
    }
    return userType;
  }

  /**
   * Sanitize text input
   */
  sanitizeText(text) {
    if (!text) throw new Error('Text is required');
    
    // Remove HTML tags and trim
    const sanitized = text.replace(/<[^>]*>/g, '').trim();
    
    if (sanitized.length === 0) {
      throw new Error('Text cannot be empty');
    }
    
    return sanitized;
  }

  // =============================================
  // EVENT LISTENERS
  // =============================================

  /**
   * Add listener for user changes
   */
  addListener(callback) {
    this.listeners.push(callback);
    return callback;
  }

  /**
   * Remove listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  /**
   * Notify all listeners of user changes
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.currentUser);
      } catch (error) {
        console.error('Error in user listener:', error);
      }
    });
  }

  // =============================================
  // GETTERS
  // =============================================

  /**
   * Get current user
   */
  getCurrentUser() {
    return this.currentUser;
  }

  /**
   * Check if user is logged in
   */
  isLoggedIn() {
    return this.currentUser !== null;
  }

  /**
   * Clear current user (logout)
   */
  clearCurrentUser() {
    this.currentUser = null;
    this.notifyListeners();
  }
}

// Create singleton instance
const userService = new UserService();
export default userService;

export function getUserLevel(points) {
  const levels = [
    { name: 'POSER', icon: '🤡', color: '#6B7280', min: 0, max: 19, approvalPower: 1, reportWeight: 1 },
    { name: 'Scene Supporter', icon: '📻', color: '#3B82F6', min: 20, max: 49, approvalPower: 1, reportWeight: 1 },
    { name: 'Active Member', icon: '🎧', color: '#10B981', min: 50, max: 99, approvalPower: 1, reportWeight: 1 },
    { name: 'Trusted Poster', icon: '🎵', color: '#8B5CF6', min: 100, max: 249, approvalPower: 1, reportWeight: 1 },
    { name: 'Scene Insider', icon: '🎤', color: '#CD7F32', min: 250, max: 499, approvalPower: 2, reportWeight: 3 },
    { name: 'Scene Veteran', icon: '🎸', color: '#C0C0C0', min: 500, max: 999, approvalPower: 2, reportWeight: 3 },
    { name: 'Scene Legend', icon: '👑', color: '#FFD700', min: 1000, max: Infinity, approvalPower: 3, reportWeight: 3 }
  ];
  return levels.find(level => points >= level.min && points <= level.max) || levels[0];
}

/**
 * Award scene points to a user for a specific reason
 */
export async function awardScenePoints(userId, points, reason, referenceId = null) {
  const { error } = await supabase.rpc('award_scene_points', {
    target_user_id: userId,
    points,
    reason,
    reference_id: referenceId
  });
  if (error) {
    console.error('Error awarding scene points:', error);
    return { success: false, error: error.message };
  }
  return { success: true };
} 